import React from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { Box } from '@mui/material'

import { useAuth } from './contexts/AuthContext'
import Layout from './components/layout/Layout'
import LoginForm from './components/auth/LoginForm'
import ResetPasswordForm from './components/auth/ResetPasswordForm'
import ChangePasswordDialog from './components/auth/ChangePasswordDialog'
import Dashboard from './components/dashboard/Dashboard'
import LoadingSpinner from './components/common/LoadingSpinner'
import ProcedimientosList from './components/procedimientos/ProcedimientosList'
import ProcedimientoForm from './components/procedimientos/ProcedimientoForm'
import ProcedimientoEdit from './components/procedimientos/ProcedimientoEdit'
import ProcedimientoView from './components/procedimientos/ProcedimientoView'
import ProcedimientosEliminados from './components/procedimientos/ProcedimientosEliminados'
import GestionesList from './components/gestiones/GestionesList'
import GestionView from './components/gestiones/GestionView'
import ConfiguracionSistema from './components/config/ConfiguracionSistema'
import UsuariosAdmin from './components/usuarios/UsuariosAdmin'
import AuditoriaViewer from './components/auditoria/AuditoriaViewer'
import AuditoriaDetailView from './components/auditoria/AuditoriaDetailView'
import LogsManagement from './components/auditoria/LogsManagement'
import ProcesosList from './components/procesos/ProcesosList'
import PerfilUsuario from './components/perfil/PerfilUsuario'
import AcercaDe from './components/about/AcercaDe'
import NotificacionesView from './components/notificaciones/NotificacionesView'
import SearchPage from './components/common/SearchPage'
import DiagramBuilder from './components/tools/DiagramBuilder'
import PublicLayout from './components/publico/PublicLayout'
import PublicHome from './components/publico/PublicHome'
import PublicProcedimientosList from './components/publico/PublicProcedimientosList'
import PublicProcedimientoDetail from './components/publico/PublicProcedimientoDetail'

import './styles/App.css'

// Rutas del portal público (accesibles sin autenticación)
const PublicRoutes = () => (
  <Routes>
    <Route path="/" element={<Navigate to="/publico" replace />} />
    <Route path="/publico" element={<PublicLayout />}>
      <Route index element={<PublicHome />} />
      <Route path="procedimientos" element={<PublicProcedimientosList />} />
      <Route path="gestion/:gestionId" element={<PublicProcedimientosList />} />
      <Route path="procedimiento/:procedimientoId" element={<PublicProcedimientoDetail />} />
      <Route path="*" element={<Navigate to="/publico" replace />} />
    </Route>
  </Routes>
)

// Página de login centrada
const LoginPage = () => (
  <Box
    sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      bgcolor: 'background.default'
    }}
  >
    <LoginForm />
  </Box>
)

function App() {
  const { user, loading, requiresPasswordChange, handlePasswordChanged } = useAuth()

  // Obtener la ruta actual (reactivo a navegación SPA)
  const location = useLocation()
  const pathname = location.pathname
  const isPublicPath = pathname.startsWith('/publico')
  const isHomePath = pathname === '/'

  // El portal público (y la raíz) no requieren autenticación
  if (isPublicPath || isHomePath) {
    return <PublicRoutes />
  }

  if (loading) {
    return <LoadingSpinner />
  }

  // Rutas públicas de autenticación (sin sesión)
  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/reset-password" element={<ResetPasswordForm />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    )
  }

  return (
    <>
      <Layout>
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/login" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/perfil" element={<PerfilUsuario />} />

          {/* Rutas protegidas por rol */}
          {(user.rol === 'administrador' || user.rol === 'editor' || user.rol === 'consultor') && (
            <>
              <Route path="/procedimientos/nuevo" element={<ProcedimientoForm />} />
              <Route path="/procedimientos/:id/editar" element={<ProcedimientoEdit />} />
            </>
          )}

          {(user.rol === 'administrador' || user.rol === 'editor') && (
            <>
              <Route path="/notificaciones" element={<NotificacionesView />} />
            </>
          )}

          {/* Rutas de auditoría para administrador y rector */}
          {(user.rol === 'administrador' || user.rol === 'rector') && (
            <>
              <Route path="/auditoria" element={<AuditoriaViewer />} />
              <Route path="/auditoria/:auditId" element={<AuditoriaDetailView />} />
            </>
          )}

          {user.rol === 'administrador' && (
            <>
              <Route path="/configuracion/sistema" element={<ConfiguracionSistema />} />
              <Route path="/configuracion/usuarios" element={<ConfiguracionSistema />} />
              <Route path="/configuracion/responsables" element={<ConfiguracionSistema />} />
              <Route path="/configuracion/definiciones" element={<ConfiguracionSistema />} />
              <Route path="/configuracion/normatividad" element={<ConfiguracionSistema />} />
              <Route path="/configuracion/conocimiento" element={<ConfiguracionSistema />} />
              <Route path="/configuracion/contextos-ia" element={<ConfiguracionSistema />} />
              <Route path="/configuracion/logs" element={<LogsManagement />} />
              <Route path="/configuracion/auditoria" element={<AuditoriaViewer />} />
              <Route path="/procedimientos/eliminados" element={<ProcedimientosEliminados />} />
            </>
          )}

          {/* Rutas de gestiones - Todos los usuarios pueden ver, permisos se manejan en los componentes */}
          <Route path="/gestiones" element={<GestionesList />} />
          <Route path="/gestiones/:id" element={<GestionView />} />

          <Route path="/procedimientos" element={<ProcedimientosList />} />
          <Route path="/procedimientos/:id" element={<ProcedimientoView />} />
          <Route path="/procesos" element={<ProcesosList />} />
          <Route path="/buscar" element={<SearchPage />} />
          <Route path="/herramientas" element={<DiagramBuilder />} />
          <Route path="/acerca-de" element={<AcercaDe />} />
          <Route path="/mi-perfil" element={<div>Mi Perfil</div>} />

          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Layout>

      {/* Modal de cambio de contraseña obligatorio */}
      <ChangePasswordDialog
        open={requiresPasswordChange}
        onPasswordChanged={handlePasswordChanged}
      />
    </>
  )
}

export default App